/**
 * `select-image` wizard step renderer (design §6) — registry-honest picker
 * over `GET /api/daytona/images` (P5-1: only registered, active images are
 * offered; there is no free-text image entry anywhere, so the P5-T1 422 path
 * is unreachable from this UI).
 *
 * Contributed by the coder feature; picked up by the P2 glob step registry
 * (`features/agents/wizard/steps/registry.ts`) with ZERO edits to P2 files —
 * the same contract the numbers feature's steps use.
 *
 * Transport (P6-T3, audit #2 closed): the selection rides into create as
 * `overrides.coderConfig.imageId` (draft.ts `collectCoderConfigOverride`) and
 * is persisted on the factory `coderConfig`; when no value is sent, the
 * backend's from-template path falls back to the template's
 * `options.defaultImage`. The UI copy still claims nothing about saving
 * (design §9.2 stance, structure unchanged) — the value is now simply true.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { CopyButton } from '@/components/common/CopyButton';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioCard } from '@/features/agents/components/RadioCard';
import type { AgentTemplateWizardStepType, WizardStepProps } from '@/features/agents/types';

import { useGetImagesQuery } from '../api/coderApi';
import { capitalizeFirst, docId } from '../lib';
import type { DockerImageView, SelectImageValue } from '../types';
import { isSelectImageValue } from '../types';

/** Radix Select forbids the empty string as an item value. */
const ALL_LANGUAGES = '__all__';

/** Draft slice builder (§9.1): `summary` is the display-ready "node-dev:20". */
function toSelectImageValue(image: DockerImageView): SelectImageValue {
  const name = image.name || image.image;
  const tag = image.tag ?? '';
  return {
    imageId: docId(image),
    name,
    tag,
    image: image.image,
    languages: image.languages ?? [],
    summary: tag ? `${name}:${tag}` : name,
  };
}

function SelectImageStep({ step, value, onChange, errors, onErrors }: WizardStepProps) {
  const stepOptions = (step.options ?? {}) as { defaultImage?: string };
  const [language, setLanguage] = useState<string>(ALL_LANGUAGES);

  const imagesQuery = useGetImagesQuery({
    language: language === ALL_LANGUAGES ? undefined : language,
  });

  // Defensive client-side filter (§6.2): the endpoint already lists active
  // entries only, but never offer a deactivated row.
  const images = useMemo(
    () => (imagesQuery.data ?? []).filter((image) => image.isActive !== false),
    [imagesQuery.data],
  );
  const selected = isSelectImageValue(value) ? value : undefined;

  // Language options = union of fetched entries (§6.2). Accumulated across
  // filter passes so the Select never collapses to the active filter alone.
  const [knownLanguages, setKnownLanguages] = useState<string[]>([]);
  useEffect(() => {
    setKnownLanguages((previous) => {
      const next = new Set(previous);
      let changed = false;
      for (const image of images) {
        for (const raw of image.languages ?? []) {
          const normalized = raw.toLowerCase();
          if (normalized && !next.has(normalized)) {
            next.add(normalized);
            changed = true;
          }
        }
      }
      return changed ? Array.from(next).sort((a, b) => a.localeCompare(b)) : previous;
    });
  }, [images]);

  // Preselect (G1): the entry matching the seed's `options.defaultImage`,
  // then the `isDefault` entry; else nothing. Attempted once per visit.
  const preselectTried = useRef(false);
  useEffect(() => {
    if (
      preselectTried.current ||
      selected ||
      imagesQuery.isLoading ||
      imagesQuery.isError ||
      images.length === 0
    ) {
      return;
    }
    preselectTried.current = true;
    const target =
      (stepOptions.defaultImage
        ? images.find((image) => image.image === stepOptions.defaultImage)
        : undefined) ?? images.find((image) => image.isDefault);
    if (target) {
      onChange(toSelectImageValue(target));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot preselect; onChange/stepOptions identities churn per frame render.
  }, [images, selected, imagesQuery.isLoading, imagesQuery.isError]);

  const listSettled = !imagesQuery.isLoading && !imagesQuery.isError;
  const catalogEmpty = listSettled && language === ALL_LANGUAGES && images.length === 0;
  const missing = step.required && !selected;

  // Continue gating via the frame's step-errors contract (P4 precedent: the
  // copy lands inline under the step — the frame tooltip is fixed P2 text).
  useEffect(() => {
    if (step.required && catalogEmpty) {
      onErrors({ image: 'An image is required. Ask your administrator to register one.' });
    } else if (missing) {
      onErrors({ image: 'Pick an image to continue.' });
    } else {
      onErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onErrors identity changes per frame render; inputs are the stable derived flags.
  }, [step.required, catalogEmpty, missing]);

  if (imagesQuery.isError) {
    return (
      <QueryErrorView
        error={imagesQuery.error}
        label="images"
        onRetry={() => void imagesQuery.refetch()}
      />
    );
  }

  if (catalogEmpty) {
    return (
      <EmptyState
        title="No images"
        description="Ask your administrator to register container images, then come back."
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">The container image the coding agent runs in.</p>

      <div className="space-y-1.5">
        <Label htmlFor={`select-image-language-${step.id}`}>Language</Label>
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger id={`select-image-language-${step.id}`} className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_LANGUAGES}>All languages</SelectItem>
            {knownLanguages.map((entry) => (
              <SelectItem key={entry} value={entry}>
                {capitalizeFirst(entry)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {imagesQuery.isLoading ? (
        <LoadingRows label="Loading images…" columns={2} rows={3} />
      ) : images.length === 0 ? (
        <EmptyState
          title={`No ${capitalizeFirst(language)} images`}
          description="Try a different language."
        />
      ) : (
        <div className="space-y-2" role="radiogroup" aria-label="Sandbox images">
          {images.map((image) => {
            const id = docId(image) || image.image;
            return (
              <RadioCard
                key={id}
                groupName={`select-image-${step.id}`}
                value={id}
                checked={Boolean(selected && selected.imageId === id)}
                onSelect={() => onChange(toSelectImageValue(image))}
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-medium">{image.name}</span>
                  {image.tag ? (
                    <span className="font-mono text-xs text-muted-foreground">{image.tag}</span>
                  ) : null}
                  {image.isDefault ? (
                    <Badge variant="outline" className="rounded-sm">
                      Default
                    </Badge>
                  ) : null}
                </span>
                {image.description ? (
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {image.description}
                  </span>
                ) : null}
                {(image.languages ?? []).length > 0 ? (
                  <span className="mt-2 flex flex-wrap gap-1">
                    {image.languages.map((entry) => (
                      <Badge key={entry} variant="outline" className="rounded-sm">
                        {capitalizeFirst(entry)}
                      </Badge>
                    ))}
                  </span>
                ) : null}
                <span className="mt-1 flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block max-w-[16rem] truncate font-mono text-xs text-muted-foreground">
                        {image.image}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs break-all">{image.image}</TooltipContent>
                  </Tooltip>
                  <CopyButton value={image.image} label="Copy image URI" />
                </span>
              </RadioCard>
            );
          })}
        </div>
      )}

      {errors.image ? (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground">
          {errors.image}
        </p>
      ) : null}
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'select-image';
export const component = SelectImageStep;
